import { ProcessAgentOption, ProcessSkillOption } from '@/api/processes-api';
import {
  XERO_EVENT_CATEGORIES,
  XERO_EVENT_TYPES,
  XERO_INVOICE_STATUSES,
  XERO_INVOICE_TYPES,
  type MessageDirection,
  type XeroEventCategory,
  type XeroEventType,
  type XeroInvoiceStatus,
  type XeroInvoiceType,
} from '@vivport/backend/types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Save } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import cronParser from 'cron-parser';

export type ProcessFormTriggerType = 'TAG_APPLIED' | 'MESSAGE_IMPORTED' | 'EMAIL_RECEIVED' | 'SCHEDULE' | 'XERO_EVENT';

const MESSAGE_DIRECTIONS: MessageDirection[] = ['INBOUND', 'OUTBOUND', 'INTERNAL'];

const XERO_EVENT_CATEGORY_LABELS: Record<XeroEventCategory, string> = {
  INVOICE: 'Invoice',
  CONTACT: 'Contact',
};

const XERO_EVENT_TYPE_LABELS: Record<XeroEventType, string> = {
  CREATE: 'Created',
  UPDATE: 'Updated',
};

const XERO_INVOICE_TYPE_LABELS: Record<XeroInvoiceType, string> = {
  ACCREC: 'Sales invoice (ACCREC)',
  ACCPAY: 'Bill (ACCPAY)',
};

function withToggled<T>(current: Set<T>, value: T, checked: boolean): Set<T> {
  const next = new Set(current);
  if (checked) {
    next.add(value);
  } else {
    next.delete(value);
  }
  return next;
}

export interface ProcessMemberOption {
  id: number;
  label: string;
}

export interface ProcessFormValues {
  name: string;
  description?: string;
  triggerType: string;
  tagId: number;
  directions: MessageDirection[];
  matchCriteria: string;
  cron: string;
  timezone: string;
  runAsMemberId: number | null;
  xeroEventCategory: XeroEventCategory;
  xeroEventTypes: XeroEventType[];
  xeroInvoiceTypes: XeroInvoiceType[];
  xeroInvoiceStatuses: XeroInvoiceStatus[];
  /** null = the process runs a platform system agent (systemAgent names it); the member has not picked an Agent row. */
  agentId: number | null;
  systemAgent?: { key: string; name: string } | null;
  skillIds?: number[];
  inputTemplate?: string;
  enabled: boolean;
}

const SYSTEM_AGENT_OPTION_VALUE = 'system';

function initialAgentSelection(values: ProcessFormValues): string {
  if (values.agentId === null && values.systemAgent) {
    return SYSTEM_AGENT_OPTION_VALUE;
  }
  return String(values.agentId || '');
}

interface Work4MeProcessFormProps {
  initialValues: ProcessFormValues;
  isSubmitting: boolean;
  onSubmit: (values: ProcessFormValues) => Promise<void>;
  submitLabel: string;
  submittingLabel: string;
  agentOptions: ProcessAgentOption[];
  skillOptions: ProcessSkillOption[];
  memberOptions?: ProcessMemberOption[];
  hubScoped?: boolean;
  disableFields?: boolean;
  showSubmit?: boolean;
  allowEnabledToggle?: boolean;
  onEnabledToggle?: (enabled: boolean) => void;
}

function isValidCronExpression(cron: string): boolean {
  try {
    cronParser.parseExpression(cron);
    return true;
  } catch {
    return false;
  }
}

export function Work4MeProcessForm({
  initialValues,
  isSubmitting,
  onSubmit,
  submitLabel,
  submittingLabel,
  agentOptions,
  skillOptions,
  memberOptions = [],
  hubScoped = true,
  disableFields = false,
  showSubmit = true,
  allowEnabledToggle = true,
  onEnabledToggle,
}: Work4MeProcessFormProps) {
  const [name, setName] = useState(initialValues.name);
  const [description, setDescription] = useState(initialValues.description ?? '');
  const [tagId, setTagId] = useState(String(initialValues.tagId || ''));
  const [directions, setDirections] = useState<Set<MessageDirection>>(new Set(initialValues.directions ?? []));
  const [matchCriteria, setMatchCriteria] = useState(initialValues.matchCriteria ?? '');
  const [cron, setCron] = useState(initialValues.cron ?? '');
  const [timezone, setTimezone] = useState(initialValues.timezone ?? '');
  const [runAsMemberId, setRunAsMemberId] = useState(String(initialValues.runAsMemberId || ''));
  const [xeroEventCategory, setXeroEventCategory] = useState<XeroEventCategory>(initialValues.xeroEventCategory);
  const [xeroEventTypes, setXeroEventTypes] = useState<Set<XeroEventType>>(new Set(initialValues.xeroEventTypes));
  const [xeroInvoiceTypes, setXeroInvoiceTypes] = useState<Set<XeroInvoiceType>>(new Set(initialValues.xeroInvoiceTypes));
  const [xeroInvoiceStatuses, setXeroInvoiceStatuses] = useState<Set<XeroInvoiceStatus>>(
    new Set(initialValues.xeroInvoiceStatuses)
  );
  const [agentId, setAgentId] = useState(initialAgentSelection(initialValues));
  const [selectedSkillIds, setSelectedSkillIds] = useState<Set<number>>(new Set(initialValues.skillIds ?? []));
  const [inputTemplate, setInputTemplate] = useState(initialValues.inputTemplate ?? '');
  const [enabled, setEnabled] = useState(initialValues.enabled);

  const triggerType = initialValues.triggerType;

  useEffect(() => {
    setName(initialValues.name);
    setDescription(initialValues.description ?? '');
    setTagId(String(initialValues.tagId || ''));
    setDirections(new Set(initialValues.directions ?? []));
    setMatchCriteria(initialValues.matchCriteria ?? '');
    setCron(initialValues.cron ?? '');
    setTimezone(initialValues.timezone ?? '');
    setRunAsMemberId(String(initialValues.runAsMemberId || ''));
    setXeroEventCategory(initialValues.xeroEventCategory);
    setXeroEventTypes(new Set(initialValues.xeroEventTypes));
    setXeroInvoiceTypes(new Set(initialValues.xeroInvoiceTypes));
    setXeroInvoiceStatuses(new Set(initialValues.xeroInvoiceStatuses));
    setAgentId(initialAgentSelection(initialValues));
    setSelectedSkillIds(new Set(initialValues.skillIds ?? []));
    setInputTemplate(initialValues.inputTemplate ?? '');
    setEnabled(initialValues.enabled);
  }, [initialValues]);

  const timezones = useMemo(() => Intl.supportedValuesOf('timeZone'), []);

  useEffect(() => {
    if (agentId === SYSTEM_AGENT_OPTION_VALUE) {
      return;
    }
    if (agentOptions.length === 0) {
      setAgentId('');
      return;
    }
    const selectedStillExists = agentOptions.some((option) => option.id.toString() === agentId);
    if (!selectedStillExists) {
      setAgentId(agentOptions[0].id.toString());
    }
  }, [agentId, agentOptions]);

  const parsedTagId = Number.parseInt(tagId, 10);
  const parsedAgentId = Number.parseInt(agentId, 10);
  const parsedRunAsMemberId = Number.parseInt(runAsMemberId, 10);
  const tagIdValid = triggerType !== 'TAG_APPLIED' || (Number.isFinite(parsedTagId) && parsedTagId > 0);
  const trimmedMatchCriteria = matchCriteria.trim();
  const matchCriteriaValid = trimmedMatchCriteria.length >= 10 && trimmedMatchCriteria.length <= 2000;
  const trimmedCron = cron.trim();
  const cronValid = trimmedCron.length > 0 && isValidCronExpression(trimmedCron);
  const runAsValid = !hubScoped || (Number.isFinite(parsedRunAsMemberId) && parsedRunAsMemberId > 0);
  const emailReceivedValid = triggerType !== 'EMAIL_RECEIVED' || matchCriteriaValid;
  const scheduleValid = triggerType !== 'SCHEDULE' || (cronValid && timezone.length > 0 && runAsValid);
  const xeroEventValid = triggerType !== 'XERO_EVENT' || (xeroEventTypes.size > 0 && runAsValid);
  const xeroInvoiceFiltersApply = triggerType === 'XERO_EVENT' && xeroEventCategory === 'INVOICE';
  const systemAgentSelected = agentId === SYSTEM_AGENT_OPTION_VALUE && Boolean(initialValues.systemAgent);
  const agentValid = systemAgentSelected || (Number.isFinite(parsedAgentId) && parsedAgentId > 0 && agentOptions.length > 0);
  const canSubmit =
    name.trim().length > 0 &&
    agentValid &&
    tagIdValid &&
    emailReceivedValid &&
    scheduleValid &&
    xeroEventValid &&
    !isSubmitting &&
    !disableFields;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    const skillIds = [...selectedSkillIds].sort((a, b) => a - b);
    await onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      triggerType,
      tagId: triggerType === 'TAG_APPLIED' ? parsedTagId : 0,
      directions: triggerType === 'MESSAGE_IMPORTED' ? MESSAGE_DIRECTIONS.filter((d) => directions.has(d)) : [],
      matchCriteria: triggerType === 'EMAIL_RECEIVED' ? trimmedMatchCriteria : '',
      cron: triggerType === 'SCHEDULE' ? trimmedCron : '',
      timezone: triggerType === 'SCHEDULE' ? timezone : '',
      runAsMemberId:
        (triggerType === 'SCHEDULE' || triggerType === 'XERO_EVENT') &&
        hubScoped &&
        Number.isFinite(parsedRunAsMemberId) &&
        parsedRunAsMemberId > 0
          ? parsedRunAsMemberId
          : null,
      xeroEventCategory,
      xeroEventTypes: triggerType === 'XERO_EVENT' ? XERO_EVENT_TYPES.filter((type) => xeroEventTypes.has(type)) : [],
      xeroInvoiceTypes: xeroInvoiceFiltersApply ? XERO_INVOICE_TYPES.filter((type) => xeroInvoiceTypes.has(type)) : [],
      xeroInvoiceStatuses: xeroInvoiceFiltersApply
        ? XERO_INVOICE_STATUSES.filter((status) => xeroInvoiceStatuses.has(status))
        : [],
      agentId: systemAgentSelected ? null : parsedAgentId,
      ...(systemAgentSelected ? { systemAgent: initialValues.systemAgent } : {}),
      ...(skillIds.length > 0 ? { skillIds } : {}),
      inputTemplate: inputTemplate.trim() || undefined,
      enabled,
    });
  };

  const runAsField = hubScoped ? (
    <div>
      <Label htmlFor="process-run-as">Run as</Label>
      <Select value={runAsMemberId} onValueChange={setRunAsMemberId} disabled={disableFields}>
        <SelectTrigger id="process-run-as" className="mt-1">
          <SelectValue placeholder="Select a hub member" />
        </SelectTrigger>
        <SelectContent>
          {memberOptions.map((option) => (
            <SelectItem key={option.id} value={option.id.toString()}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="mt-1 text-xs text-muted-foreground">
        {triggerType === 'XERO_EVENT'
          ? 'Runs fired by Xero events execute as this member.'
          : 'Scheduled runs execute as this member.'}
      </p>
      {memberOptions.length === 0 ? (
        <p className="mt-1 text-xs text-warning">No hub members are available to run as.</p>
      ) : null}
    </div>
  ) : null;

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="process-name">Name</Label>
        <Input id="process-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" disabled={disableFields} />
      </div>

      <div>
        <Label htmlFor="process-description">Description</Label>
        <Textarea
          id="process-description"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="mt-1 resize-none"
          disabled={disableFields}
        />
      </div>

      {triggerType === 'TAG_APPLIED' ? (
        <div>
          <Label htmlFor="process-tag-id">Trigger Tag ID</Label>
          <Input
            id="process-tag-id"
            type="number"
            min={1}
            value={tagId}
            onChange={(e) => setTagId(e.target.value)}
            className="mt-1"
            disabled={disableFields}
          />
        </div>
      ) : null}

      {triggerType === 'EMAIL_RECEIVED' ? (
        <div>
          <Label htmlFor="process-match-criteria">Match criteria</Label>
          <Textarea
            id="process-match-criteria"
            rows={4}
            value={matchCriteria}
            onChange={(e) => setMatchCriteria(e.target.value)}
            className="mt-1 resize-none"
            placeholder="Example: Invoices or payment reminders from our suppliers."
            disabled={disableFields}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Describe in plain language which incoming emails should run this process. An AI router reads only sender,
            subject, and snippet — it is deliberately generous; the process itself makes the final call and records a
            Skipped run when an email doesn&apos;t truly apply.
          </p>
          {trimmedMatchCriteria.length > 0 && trimmedMatchCriteria.length < 10 ? (
            <p className="mt-1 text-xs text-warning">Describe the rule in at least 10 characters.</p>
          ) : null}
          {trimmedMatchCriteria.length > 2000 ? (
            <p className="mt-1 text-xs text-warning">Keep the rule under 2000 characters.</p>
          ) : null}
        </div>
      ) : null}

      {triggerType === 'SCHEDULE' ? (
        <div className="space-y-4">
          <div>
            <Label htmlFor="process-cron">Cron expression</Label>
            <Input
              id="process-cron"
              value={cron}
              onChange={(e) => setCron(e.target.value)}
              className="mt-1 font-mono"
              placeholder="0 9 * * 1"
              disabled={disableFields}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Standard cron syntax: minute, hour, day of month, month, day of week.
            </p>
            {trimmedCron.length > 0 && !cronValid ? (
              <p className="mt-1 text-xs text-warning">Not a valid cron expression.</p>
            ) : null}
          </div>

          <div>
            <Label htmlFor="process-timezone">Timezone</Label>
            <Select value={timezone} onValueChange={setTimezone} disabled={disableFields}>
              <SelectTrigger id="process-timezone" className="mt-1">
                <SelectValue placeholder="Select an IANA timezone" />
              </SelectTrigger>
              <SelectContent>
                {timezones.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              The cron expression is evaluated in this timezone.
            </p>
          </div>

          {runAsField}
        </div>
      ) : null}

      {triggerType === 'XERO_EVENT' ? (
        <div className="space-y-4">
          <div>
            <Label htmlFor="process-xero-category">Xero record</Label>
            <Select
              value={xeroEventCategory}
              onValueChange={(value) => setXeroEventCategory(value as XeroEventCategory)}
              disabled={disableFields}
            >
              <SelectTrigger id="process-xero-category" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {XERO_EVENT_CATEGORIES.map((category) => (
                  <SelectItem key={category} value={category}>
                    {XERO_EVENT_CATEGORY_LABELS[category]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              Runs when the connected Xero organisation reports a change to this kind of record. The record is read
              live for the run; nothing from Xero is stored.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Event types</Label>
            <div className="rounded border p-2 space-y-2">
              {XERO_EVENT_TYPES.map((type) => (
                <label key={type} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={xeroEventTypes.has(type)}
                    disabled={disableFields}
                    onCheckedChange={(checked) => setXeroEventTypes((prev) => withToggled(prev, type, checked === true))}
                  />
                  <span>{XERO_EVENT_TYPE_LABELS[type]}</span>
                </label>
              ))}
            </div>
            {xeroEventTypes.size === 0 ? (
              <p className="text-xs text-warning">Select at least one event type.</p>
            ) : null}
          </div>

          {xeroEventCategory === 'INVOICE' ? (
            <>
              <div className="space-y-2">
                <Label>Invoice types</Label>
                <p className="text-xs text-muted-foreground">Leave all unchecked to match every type.</p>
                <div className="rounded border p-2 space-y-2">
                  {XERO_INVOICE_TYPES.map((type) => (
                    <label key={type} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={xeroInvoiceTypes.has(type)}
                        disabled={disableFields}
                        onCheckedChange={(checked) =>
                          setXeroInvoiceTypes((prev) => withToggled(prev, type, checked === true))
                        }
                      />
                      <span>{XERO_INVOICE_TYPE_LABELS[type]}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Invoice statuses</Label>
                <p className="text-xs text-muted-foreground">Leave all unchecked to match every status.</p>
                <div className="rounded border p-2 space-y-2">
                  {XERO_INVOICE_STATUSES.map((status) => (
                    <label key={status} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={xeroInvoiceStatuses.has(status)}
                        disabled={disableFields}
                        onCheckedChange={(checked) =>
                          setXeroInvoiceStatuses((prev) => withToggled(prev, status, checked === true))
                        }
                      />
                      <span>{status}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          ) : null}

          {runAsField}
        </div>
      ) : null}

      {triggerType === 'MESSAGE_IMPORTED' ? (
        <div className="space-y-2">
          <Label>Message directions</Label>
          <p className="text-xs text-muted-foreground">
            Runs when an imported message matches one of the selected directions. Leave all unchecked to match every direction.
          </p>
          <div className="rounded border p-2 space-y-2">
            {MESSAGE_DIRECTIONS.map((direction) => (
              <label key={direction} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={directions.has(direction)}
                  disabled={disableFields}
                  onCheckedChange={(checked) => {
                    setDirections((prev) => {
                      const next = new Set(prev);
                      if (checked === true) {
                        next.add(direction);
                      } else {
                        next.delete(direction);
                      }
                      return next;
                    });
                  }}
                />
                <span>{direction}</span>
              </label>
            ))}
          </div>
        </div>
      ) : null}

      <div>
        <Label htmlFor="process-agent-id">Agent</Label>
        <Select value={agentId} onValueChange={setAgentId} disabled={disableFields}>
          <SelectTrigger id="process-agent-id" className="mt-1">
            <SelectValue placeholder="Select an enabled agent" />
          </SelectTrigger>
          <SelectContent>
            {initialValues.systemAgent ? (
              <SelectItem value={SYSTEM_AGENT_OPTION_VALUE}>System · {initialValues.systemAgent.name}</SelectItem>
            ) : null}
            {agentOptions.map((option) => (
              <SelectItem key={option.id} value={option.id.toString()}>
                {option.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {agentOptions.length === 0 && !initialValues.systemAgent ? (
          <p className="mt-1 text-xs text-warning">No enabled agents are available.</p>
        ) : null}
      </div>

      {skillOptions.length > 0 ? (
        <div className="space-y-2">
          <Label>Additional skills at runtime (optional)</Label>
          <p className="text-xs text-muted-foreground">
            Merged into the agent prompt for this process, after any skills linked on the agent.
          </p>
          <div className="max-h-40 overflow-y-auto rounded border p-2 space-y-2">
            {skillOptions.map((skill) => (
              <label key={skill.id} className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={selectedSkillIds.has(skill.id)}
                  disabled={disableFields}
                  onCheckedChange={(checked) => {
                    setSelectedSkillIds((prev) => {
                      const next = new Set(prev);
                      if (checked === true) {
                        next.add(skill.id);
                      } else {
                        next.delete(skill.id);
                      }
                      return next;
                    });
                  }}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium">{skill.name}</span>
                  {skill.description ? (
                    <span className="block text-xs text-muted-foreground">{skill.description}</span>
                  ) : null}
                </span>
              </label>
            ))}
          </div>
        </div>
      ) : null}

      <div>
        <Label htmlFor="process-template">Input Template (optional)</Label>
        <Textarea
          id="process-template"
          rows={4}
          value={inputTemplate}
          onChange={(e) => setInputTemplate(e.target.value)}
          className="mt-1 resize-none"
          placeholder={
            triggerType === 'MESSAGE_IMPORTED'
              ? 'Example: Imported {{provider}} message {{contentId}} ({{direction}}).'
              : triggerType === 'EMAIL_RECEIVED'
              ? 'Example: Handle the email {{subject}} from {{fromAddress}}.'
              : triggerType === 'SCHEDULE'
              ? 'Example: Run the weekly review for hub {{hubId}}.'
              : triggerType === 'XERO_EVENT'
              ? 'Example: Check Xero {{eventCategory}} {{resourceId}} against the linked file.'
              : 'Example: Tag {{tagId}} was applied to content {{contentId}}.'
          }
          disabled={disableFields}
        />
        {triggerType === 'MESSAGE_IMPORTED' ? (
          <p className="mt-1 text-xs text-muted-foreground">
            Available placeholders: {'{{contentId}}'}, {'{{provider}}'}, {'{{direction}}'}.
          </p>
        ) : null}
        {triggerType === 'EMAIL_RECEIVED' ? (
          <p className="mt-1 text-xs text-muted-foreground">
            Available placeholders: {'{{messageContentId}}'}, {'{{subject}}'}, {'{{fromAddress}}'}, {'{{snippet}}'}, {'{{receivedAt}}'}.
          </p>
        ) : null}
        {triggerType === 'SCHEDULE' ? (
          <p className="mt-1 text-xs text-muted-foreground">
            Available placeholders: {'{{processId}}'}, {'{{firedAt}}'}, {'{{hubId}}'}.
          </p>
        ) : null}
        {triggerType === 'XERO_EVENT' ? (
          <p className="mt-1 text-xs text-muted-foreground">
            Available placeholders: {'{{eventCategory}}'}, {'{{eventType}}'}, {'{{resourceId}}'}, {'{{eventDateUtc}}'}, {'{{tenantId}}'}.
          </p>
        ) : null}
      </div>

      <div className="flex items-center justify-between rounded border p-3">
        <div>
          <p className="text-sm font-medium">Enabled</p>
          <p className="text-xs text-muted-foreground">Disabled processes stay stored but will not trigger.</p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(checked) => {
            setEnabled(checked);
            onEnabledToggle?.(checked);
          }}
          disabled={!allowEnabledToggle || isSubmitting}
        />
      </div>

      {showSubmit ? (
        <Button className="w-full gap-2" onClick={handleSubmit} disabled={!canSubmit}>
          <Save className="h-4 w-4" />
          {isSubmitting ? submittingLabel : submitLabel}
        </Button>
      ) : null}
    </div>
  );
}
